/**
 * Single source of truth for pre-auth / auth-flow routes.
 *
 * These pages must render WITHOUT the authenticated app: no permission gate
 * (permission-check) and no offline-first dashboard boot (PageContent). A
 * visitor here has no bearer, so booting the SyncEngine would 401 on every
 * resource and freeze the page behind the "Setting up your workspace" overlay.
 *
 * Keep aligned with the public routes declared in router/shared.tsx. Both gates
 * consume isPublicRoute so the lists cannot drift — the bug this guards against
 * was the shell gate listing only /login + /signup, stranding /recovery et al.
 */
export const PUBLIC_ROUTE_PREFIXES = [
  "/login",
  "/signup",
  "/forgot-password",
  "/password-reset-sent",
  "/recovery",
  "/confirm-email",
] as const;

/**
 * True for any pre-auth auth-flow route, including token sub-paths like
 * /recovery/<token> and /confirm-email/<id>. Does NOT include the landing page
 * ("/"), which callers handle separately where relevant.
 */
export function isPublicRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return PUBLIC_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Router path patterns for the public/auth-flow routes (the landing index route
 * aside). router/shared.tsx builds its public route objects from this list, so
 * the router and the gate prefixes share one source. The guard test asserts
 * every pattern here is covered by isPublicRoute — add a public route the gates
 * don't recognise and CI fails instead of the page silently freezing behind the
 * offline-first boot.
 */
export const PUBLIC_ROUTER_PATHS = [
  "login",
  "signup",
  "confirm-email/:id",
  "forgot-password",
  "password-reset-sent",
  "recovery/:id",
  "recovery/success",
] as const;

export type PublicRouterPath = (typeof PUBLIC_ROUTER_PATHS)[number];
